/**
 * Lightweight Client-Side Currency Estimator for Shopify (CAD Base)
 * Pure display layer — does NOT touch Shopify Cart, Checkout, or Backend Data.
 */
(function () {
  'use strict';

  // --- Configuration ---
  const BASE_CURRENCY = 'CAD';
  const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12 Hours Cache
  const STORAGE_KEY_RATES = 'cvc_exchange_rates_v1';
  const STORAGE_KEY_GEO = 'cvc_user_geo_v1';
  
  // Primary & Fallback Exchange Rate APIs (Free, No API Key Required)
  const RATE_API_PRIMARY = `https://open.er-api.com/v6/latest/${BASE_CURRENCY}`;
  const GEO_API = 'https://ipapi.co/json/';

  // --- Helpers ---
  async function fetchWithTimeout(url, timeoutMs = 3000) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (e) {
      clearTimeout(timeoutId);
      return null;
    }
  }

  // Detect visitor local currency code
  async function detectUserCurrency() {
    // 1. Check local cache
    try {
      const cached = localStorage.getItem(STORAGE_KEY_GEO);
      if (cached) {
        const { currency, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL_MS && currency) {
          return currency;
        }
      }
    } catch (e) {}

    let currency = null;

    // 2. IP-based Geolocation lookup
    try {
      const res = await fetchWithTimeout(GEO_API, 2500);
      if (res && res.ok) {
        const data = await res.json();
        if (data && data.currency && data.currency !== 'undefined') {
          currency = data.currency;
        }
      }
    } catch (e) {}

    // 3. Fallback: Browser locale mapping if IP geo fails
    if (!currency) {
      try {
        const locale = navigator.language || navigator.userLanguage || 'en-US';
        const region = locale.split('-')[1];
        const commonMap = {
          US: 'USD', GB: 'GBP', AU: 'AUD', NZ: 'NZD',
          DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', IE: 'EUR',
          JP: 'JPY', CH: 'CHF', IN: 'INR', MX: 'MXN', BR: 'BRL'
        };
        if (region && commonMap[region.toUpperCase()]) {
          currency = commonMap[region.toUpperCase()];
        }
      } catch (e) {}
    }

    // Cache valid result
    if (currency) {
      try {
        localStorage.setItem(STORAGE_KEY_GEO, JSON.stringify({
          currency: currency,
          timestamp: Date.now()
        }));
      } catch (e) {}
    }

    return currency;
  }

  // Fetch Exchange Rate for detected currency
  async function getExchangeRate(targetCurrency) {
    if (!targetCurrency || targetCurrency === BASE_CURRENCY) return null;

    // Check local storage cache
    try {
      const cached = localStorage.getItem(STORAGE_KEY_RATES);
      if (cached) {
        const { rates, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL_MS && rates && rates[targetCurrency]) {
          return rates[targetCurrency];
        }
      }
    } catch (e) {}

    // Fetch live rates
    try {
      const res = await fetchWithTimeout(RATE_API_PRIMARY, 3500);
      if (res && res.ok) {
        const data = await res.json();
        if (data && data.result === 'success' && data.rates && data.rates[targetCurrency]) {
          localStorage.setItem(STORAGE_KEY_RATES, JSON.stringify({
            rates: data.rates,
            timestamp: Date.now()
          }));
          return data.rates[targetCurrency];
        }
      }
    } catch (e) {}

    return null;
  }

  // Parse numerical CAD amount from HTML element text or data attribute
  function parseCADPrice(element) {
    if (element.dataset.cadPrice) {
      const val = parseFloat(element.dataset.cadPrice);
      if (!isNaN(val)) return val;
    }
    const text = element.innerText || element.textContent || '';
    // Strip non-numeric standard characters while retaining decimals
    const match = text.replace(/,/g, '').match(/\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : null;
  }

  // Format currency according to visitor's locale rules
  function formatConvertedPrice(amount, currencyCode) {
    try {
      const locale = navigator.language || 'en-US';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      return `${amount.toFixed(2)} ${currencyCode}`;
    }
  }

  // Inject CSS rules directly to avoid additional asset request overhead
  function injectStyles() {
    if (document.getElementById('cvc-widget-styles')) return;
    const style = document.createElement('style');
    style.id = 'cvc-widget-styles';
    style.textContent = `
      .cvc-converted-estimate {
        font-size: 0.88em;
        color: #555555;
        font-weight: normal;
        margin-left: 6px;
        white-space: nowrap;
      }
      .cvc-disclaimer-note {
        display: block;
        font-size: 0.72rem;
        color: #666666;
        margin-top: 3px;
        line-height: 1.25;
        font-style: italic;
      }
    `;
    document.head.appendChild(style);
  }

  // Main DOM conversion step
  function applyConversions(rate, targetCurrency) {
    injectStyles();
    
    // Support previous app classes or generic conversion markup
    const selectors = '.cvc-money, [data-currency-convert]';
    const elements = document.querySelectorAll(selectors);

    elements.forEach(el => {
      if (el.dataset.cvcProcessed === 'true') return;

      const cadAmount = parseCADPrice(el);
      if (cadAmount === null || cadAmount <= 0) return;

      const convertedAmount = cadAmount * rate;
      const formattedConverted = formatConvertedPrice(convertedAmount, targetCurrency);

      // Inject conversion display
      const estimateSpan = document.createElement('span');
      estimateSpan.className = 'cvc-converted-estimate';
      estimateSpan.setAttribute('aria-label', `Approximately ${formattedConverted}`);
      estimateSpan.textContent = `(approx. ${formattedConverted})`;

      el.appendChild(estimateSpan);

      // Add quiet disclaimer below price if requested or default
      if (el.dataset.showDisclaimer !== 'false') {
        const disclaimer = document.createElement('span');
        disclaimer.className = 'cvc-disclaimer-note';
        disclaimer.textContent = '*Estimate only. Order charged in CAD at checkout.';
        
        if (el.parentNode && !el.parentNode.querySelector('.cvc-disclaimer-note')) {
          el.parentNode.insertBefore(disclaimer, el.nextSibling);
        }
      }

      el.dataset.cvcProcessed = 'true';
    });
  }

  // Controller
  async function init() {
    try {
      const targetCurrency = await detectUserCurrency();
      
      // If detection fails, or user is already local CAD visitor, exit silently
      if (!targetCurrency || targetCurrency === BASE_CURRENCY) return;

      const rate = await getExchangeRate(targetCurrency);
      if (!rate) return; // Silent exit if exchange rate API unavailable

      // Apply to existing markup
      applyConversions(rate, targetCurrency);

      // Observe DOM changes (for dynamic AJAX cart drawers or Shopify variant selectors)
      const observer = new MutationObserver(() => {
        applyConversions(rate, targetCurrency);
      });

      observer.observe(document.body, { childList: true, subtree: true });

    } catch (e) {
      // Catch-all to prevent storefront script runtime errors
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();